<template>
    <draggable ref="draggableInstance" v-bind="attrs">
        <template #header>
            <slot name="header"/>
        </template>

        <template #item="slotData">
            <component
                :is="itemTag"
                :class="typeof itemClass === 'function' ? itemClass(slotData) : itemClass"
                v-bind="resolveItemProps(slotData)"
                v-on="resolveItemEventListeners(slotData)"
                v-tooltip="resolveItemToolTip(slotData)"
            >
                <slot name="item" v-bind="slotData"/>
            </component>
        </template>

        <template #footer>
            <slot name="footer"/>
        </template>
    </draggable>
</template>

<script setup lang="ts">
    import {ref, useAttrs} from 'vue';
    import draggable from 'vuedraggable';
    import {usePortuna} from '@/composables/portuna';

    const portuna = usePortuna();

    defineOptions({
        inheritAttrs: true,
    });

    const props = withDefaults(defineProps<{
        itemTag?: string
        itemClass?: AppDraggableItemClass
        itemProps?: AppDraggableItemProps
        itemEvents?: AppDraggableItemEvents
        itemTooltip?: AppDraggableItemToolTip
    }>(), {
        itemTag: 'div',
        itemClass: '',
        itemProps: () => ({}),
        itemEvents: () => ({}),
        itemTooltip: undefined,
    });

    const attrs = useAttrs();

    function getValue<T>(value: AppDraggableObjectOrFunction<T>, slotData: AppDraggableSlotData): T
    {
        return typeof value === 'function' ? (value as any)(slotData) : value;
    }

    function applyEventModifiers(
        handler: Function | undefined,
        slotData: AppDraggableSlotData,
        modifiers?: { prevent?: boolean; stop?: boolean },
    )
    {
        return (event: Event) =>
        {
            if (modifiers?.prevent)
            {
                event.preventDefault();
            }

            if (modifiers?.stop)
            {
                event.stopPropagation();
            }

            return handler?.(event, slotData);
        };
    }

    function resolveItemProps(slotData: AppDraggableSlotData)
    {
        return getValue(props.itemProps, slotData);
    }

    function resolveItemToolTip(slotData: AppDraggableSlotData)
    {
        return getValue(props.itemTooltip, slotData);
    }

    function resolveItemEventListeners(slotData: AppDraggableSlotData)
    {
        const eventConfig                    = getValue(props.itemEvents, slotData);
        const listeners: Record<string, any> = {};

        for (const [eventName, value] of Object.entries(eventConfig))
        {
            if (Array.isArray(value))
            {
                const [handler, modifiers] = value as unknown as [Function, { prevent?: boolean; stop?: boolean }];
                listeners[eventName]       = applyEventModifiers(handler, slotData, modifiers);
            }
            else
            {
                listeners[eventName] = (event: Event) => (value as Function)?.(event, slotData);
            }
        }

        return listeners;
    }

    const slots = defineSlots<{
        item(props: AppDraggableSlotData): any
        header(): any
        footer(): any
    }>();

    const draggableInstance = ref<Optional<typeof draggable>>();

    defineExpose(Object.assign({}, portuna, {
        componentName: 'AppDraggable',
        draggableInstance,
    }));
</script>